import React from "react";
import _ from "lodash";
import { useTranslation } from "react-i18next";
import ToggleLinksInput from "../../../parts/inputs/toggle-links-input";
import fromIntensity from "../../../utils/get-zone";
import { RPE_STANDARD_TABLE } from "../../../constants";
import { useRillContext } from "../../../rill/RillReact/share/base";

const REP_RANGE = _.range(1, 13);
const RPE_RANGE = _.reverse(_.range(5, 10.5, 0.5));
const CALC_REP_RANGE = _.range(-12, 23);
const CALC_RPE_RANGE = _.reverse(_.range(-7, 22.5, 0.5));
const MAX_CALC_RPE = _.max(CALC_RPE_RANGE);
const MIN_CALC_RPE = _.min(CALC_RPE_RANGE);
const MAX_CALC_REP = _.max(CALC_REP_RANGE);
const MIN_CALC_REP = _.min(CALC_REP_RANGE);

function View(props) {
  const { t } = useTranslation("dashboard");
  const { locals } = useRillContext();

  _.defaults(locals.query, {
    type: "squat",
    display: "repMax",
  });

  const { snapshot, query, momentTZ } = locals;
  const activeFormula = _.get(snapshot, "data.formula", "standard");
  const activeMetrics = _.cloneDeep(
    _.get(snapshot, ["data", "exercisesRPETable", query.type]) || {}
  );
  const activeTable =
    activeFormula === "standard"
      ? RPE_STANDARD_TABLE
      : calculateEstimates(activeMetrics);
  const activeRepMax = _.get(snapshot, "data.repMax", 0);

  return (
    <div className="Main">
      <h1 className="alt">{t("rpeTable.title")}</h1>
      <p>
        {t("rpeTable.description")}
      </p>

      <div className="RPE-Selector">
        <div className="select-label">
          <b className={query.type}>{_.capitalize(query.type)}</b> {t("rpeTable.oneRm")}{" "}
          {locals.withWeight(locals.fromMetric(activeRepMax))}
        </div>
      </div>

      <div className="Table-Head">
        <div className="right-side">
          <div className="group">
            <ToggleLinksInput
              name="display"
              options={[
                { text: t("rpeTable.percent1rm"), value: "repMax" },
                { text: t("rpeTable.kg"), value: "metric" },
                { text: t("rpeTable.lbs"), value: "imperial" },
              ]}
            />
          </div>
        </div>
        <div className="left-side">
          <div className="group">
            <ToggleLinksInput
              className="tabs"
              name="type"
              options={[
                { text: t("rpeTable.squat"), value: "squat" },
                { text: t("rpeTable.bench"), value: "bench" },
                { text: t("rpeTable.deadlift"), value: "deadlift" },
              ]}
            />
          </div>
        </div>
      </div>

      <div className="Table-Wrapper">
        <table className="Table RPE-Table">
          <thead>
            <tr>
              <th>{t("rpeTable.rpe")}</th>
              <th>{t("rpeTable.oneRep")}</th>
              <th>2</th>
              <th>3</th>
              <th>4</th>
              <th>5</th>
              <th>6</th>
              <th>7</th>
              <th>8</th>
              <th>9</th>
              <th>10</th>
              <th>11</th>
              <th>12</th>
            </tr>
          </thead>
          <tbody>
            {_.map(RPE_RANGE, (rpe) => {
              if (!activeTable[rpe]) return;
              return (
                <tr key={rpe}>
                  <td className="bold">{rpe}</td>
                  {_.map(REP_RANGE, (rep) => {
                    const result = _.get(activeTable, [rpe, rep]) || {};
                    return (
                      <td key={rep} className={result.zone}>
                        {formatIntensity(result.intensity)}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="Metrics-Foot"></div>
    </div>
  );

  function formatIntensity(intensity) {
    if (!intensity || intensity <= 0) return "-";
    if (query.display === "repMax") return `${intensity}%`;
    return locals.withWeight(
      locals.fromMetric((intensity * activeRepMax) / 100, query.display),
      query.display
    );
  }
}

/**
 * Estimates RPE table based one three strategies.
 */
function calculateEstimates(table) {
  let found = 0;

  // Blow up using up / right and down / left strategory.
  found += blowOut(table);

  // Estimate values by the average for the row.
  const foundInRow = (found += estimateRow(table));
  // If we find anything in row estimates then we blowout again.
  if (foundInRow) found += blowOut(table);

  // Estimate values by the average for the column.
  found += estimateColumn(table);

  // If we found any points this run then we try again.
  if (found) return calculateEstimates(table);

  // Otherwise we can't calculate anymore.
  return table;
}

/**
 * Calculates rpe estimates by blowing out the table.
 * Shifts existing data points diagonally (up and right, down and left).
 */
function blowOut(table) {
  let found = 0;
  _.forOwn(table, (reps, rpe) => {
    _.forOwn(reps, (data, rep) => {
      if (!data || !data.intensity) return;

      let posX = null;
      let posY = null;

      // up and right
      posX = rep;
      posY = rpe;
      while (
        ++posX < MAX_CALC_REP &&
        ++posY < MAX_CALC_RPE &&
        !_.get(table, [posY, posX])
      ) {
        _.set(table, [posY, posX], {
          intensity: data.intensity,
          zone: fromIntensity(data.intensity, posX),
        });
        found++;
      }

      // down and left
      posX = rep;
      posY = rpe;
      while (
        --posX > MIN_CALC_REP &&
        --posY > MIN_CALC_RPE &&
        !_.get(table, [posY, posX])
      ) {
        _.set(table, [posY, posX], {
          intensity: data.intensity,
          zone: fromIntensity(data.intensity, posX),
        });
        found++;
      }
    });
  });

  return found;
}

/**
 * Estimates data to right of a row (don't guess to the left).
 */
function estimateRow(table) {
  let found = 0;
  _.forOwn(table, (reps, rpe) => {
    rpe = Number(rpe);
    let previousIntensity = null;
    let previousRep = null;
    let activeDelta = null;
    let estimateReps = [];
    _.forEach(CALC_REP_RANGE, (rep, i) => {
      const data = _.get(reps, rep);

      if (data) {
        if (previousIntensity) {
          activeDelta =
            Math.abs(previousIntensity - data.intensity) /
            Math.abs(previousRep - rep);
        }

        previousRep = rep;
        previousIntensity = data.intensity;
      } else if (previousIntensity) {
        estimateReps.push(rep);
      }

      if (activeDelta && (data || i === CALC_REP_RANGE.length - 1)) {
        _.forEach(estimateReps, (rep) => {
          found++;
          const intensity = _.round(
            previousIntensity - (rep - previousRep) * activeDelta,
            1
          );
          _.set(table, [rpe, rep], {
            zone: fromIntensity(intensity, rep),
            intensity,
          });
        });

        estimateReps = [];
      }
    });
  });

  return found;
}

/**
 * Estimates data down in a column (don't guess to the above).
 */
function estimateColumn(table) {
  let found = 0;
  _.forEach(CALC_REP_RANGE, (rep) => {
    let previousIntensity = null;
    let previousRpe = null;
    let activeDelta = null;
    let estimateRpes = [];

    _.forEach(RPE_RANGE, (rpe, i) => {
      const data = _.get(table, [rpe, rep]);

      if (data) {
        if (previousIntensity) {
          activeDelta =
            Math.abs(previousIntensity - data.intensity) /
            Math.abs(previousRpe - rpe);
        }

        previousRpe = rpe;
        previousIntensity = data.intensity;
      } else if (previousIntensity) {
        estimateRpes.push(rpe);
      }

      if (activeDelta && (data || i === RPE_RANGE.length - 1)) {
        _.forEach(estimateRpes, (rpe) => {
          found++;
          const intensity = _.round(
            previousIntensity - (previousRpe - rpe) * activeDelta,
            1
          );
          _.set(table, [rpe, rep], {
            zone: fromIntensity(intensity, rep),
            intensity,
          });
        });

        estimateRpes = [];
      }
    });
  });

  return found;
}

export default View;
